// ==================== SMS ENGAGEMENT COMPONENT ====================

import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  MessageSquare,
  ArrowDownLeft,
  ArrowUpRight,
  Layers,
  Send,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { SmsAnalyticsData } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface SmsEngagementProps {
  data: SmsAnalyticsData | null;
  isLoading?: boolean;
}

// ==================== METRIC CARD ====================

const MetricCard = ({
  label,
  value,
  icon: Icon,
  color,
  suffix = '',
}: {
  label: string;
  value: number | string;
  icon: typeof TrendingUp;
  color: string;
  suffix?: string;
}) => (
  <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', color)}>
      <Icon size={14} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-(--color-muted-gray) truncate">{label}</p>
      <p className="text-sm font-bold text-white">
        {value}{suffix}
      </p>
    </div>
  </div>
);

// ==================== COMPONENT ====================

export const SmsEngagement = ({ data, isLoading = false }: SmsEngagementProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base flex items-center gap-1.5">
              <MessageSquare size={14} className="text-purple-400" />
              SMS Engagement
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid grid-cols-2 gap-2 animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-(--color-border-gray) rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const totalMessages = (data?.totals.sent || 0) + (data?.totals.delivered || 0) + (data?.totals.failed || 0) + (data?.totals.pending || 0);
  const outboundPercentage = totalMessages > 0 
    ? Math.round((data?.byDirection.outbound || 0) / ((data?.byDirection.outbound || 0) + (data?.byDirection.inbound || 0)) * 100) || 0
    : 0;
  const inboundPercentage = 100 - outboundPercentage;

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <CardTitle className="text-base flex items-center gap-1.5">
            <MessageSquare size={14} className="text-purple-400" />
            SMS Engagement
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 gap-2">
              <MetricCard
                label="Delivery Rate"
                value={data?.totals.deliveryRate || 0}
                suffix="%"
                icon={TrendingUp}
                color="bg-green-500"
              />
              <MetricCard
                label="Total Messages"
                value={totalMessages}
                icon={Send}
                color="bg-purple-500"
              />
              <MetricCard
                label="Total Segments"
                value={data?.totalSegments || 0}
                icon={Layers}
                color="bg-blue-500"
              />
              <MetricCard
                label="Avg Segments/Msg"
                value={data?.avgSegmentsPerMessage || 0}
                icon={MessageSquare}
                color="bg-(--color-signal-orange)"
              />
            </div>

            {/* Message Direction */}
            <div className="p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <p className="text-[10px] text-(--color-muted-gray) mb-2">Message Direction</p>
              <div className="flex items-center gap-2">
                {/* Progress bar */}
                <div className="flex-1 h-2 bg-(--color-border-gray) rounded-full overflow-hidden flex">
                  <div 
                    className="h-full bg-purple-500 transition-all duration-500"
                    style={{ width: `${outboundPercentage}%` }}
                  />
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${inboundPercentage}%` }}
                  />
                </div>
              </div>
              <div className="flex justify-between mt-1.5 text-[10px]">
                <div className="flex items-center gap-1">
                  <ArrowUpRight size={10} className="text-purple-400" />
                  <span className="text-(--color-soft-gray)">
                    Outbound: {data?.byDirection.outbound || 0} ({outboundPercentage}%)
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ArrowDownLeft size={10} className="text-green-400" />
                  <span className="text-(--color-soft-gray)">
                    Inbound: {data?.byDirection.inbound || 0} ({inboundPercentage}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SmsEngagement;
